import { FileSize } from "@enums/fileSize";
import { SignatureModule } from "@enums/signatureModule";
import { ValidationStatus } from "@enums/validationStatus";
import { ICreateMediaItem } from "@interfaces/createMediaItem";
import { IFileForUpload } from "@interfaces/Shared/file";
import { IFormValues } from "@interfaces/Shared/formValues";
import { deleteFile } from "@services/Shared/fileDelete/fileDeleteService";
import AssetSizeError from "@services/Shared/fileUpload/errors/AssetSizeError";
import FileUploadService, {
  UploadEvent,
} from "@services/Shared/fileUpload/fileUploadService";
import { useState } from "react";

interface IUseFileUploadReturn {
  getFilesUrls: (
    uuid: string,
    module: SignatureModule,
    filesForUpload: IFileForUpload[],
    formValues: IFormValues,
    isGallery?: boolean
  ) => Promise<IFormValues | undefined>;
  deleteFileFromStorage: (
    uuid: string,
    fileLink: string,
    module: SignatureModule
  ) => Promise<void>;
  isUploading: boolean;
  validationMessage?: string;
  status: ValidationStatus;
  totalFiles: number;
  totalSize: number;
  uploadedFiles: number;
  uploadedSize: number;
  handleResetFileUpload: () => void;
}

const useFileUpload = (FileSize: FileSize): IUseFileUploadReturn => {
  const [validationMessage, setValidationMessage] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [status, setStatus] = useState<ValidationStatus>(
    ValidationStatus.default
  );
  const [totalFiles, setTotalFiles] = useState(0);
  const [totalSize, setTotalSize] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState(0);
  const [uploadedSize, setUploadedSize] = useState(0);

  let galleryUrlsArray: ICreateMediaItem[] = [];

  const getFilesUrls = async (
    uuid: string,
    module: SignatureModule,
    filesForUpload: IFileForUpload[],
    formValues: IFormValues,
    isGallery?: boolean
  ) => {
    const fileUploadService = new FileUploadService(uuid, module);

    let updatedValues = formValues;

    try {
      await fileUploadService
        .on(UploadEvent.UPLOAD_START, handleUploadStart)
        .on(UploadEvent.UPLOADED_BYTES, handleOnUploadedBytes)
        .on(UploadEvent.UPLOADED_FILE, ({ field, url }) => {
          handleUploadedFiles(updatedValues, field, url, isGallery);
          setUploadedFiles((prevState) => prevState + 1);
        })
        .on(UploadEvent.UPLOAD_SUCCESS, handleUploadSuccess)
        .uploadFiles(filesForUpload, FileSize);

      return updatedValues;
    } catch (e) {
      if (e instanceof AssetSizeError) {
        setStatus(ValidationStatus.error);
        setValidationMessage("File size has exceeded max size allowed.");
      } else {
        console.error(e);
        setValidationMessage("An error has occurred. Try again.");
        setStatus(ValidationStatus.error);
      }
    }
  };

  const deleteFileFromStorage = async (
    uuid: string,
    fileLink: string,
    module: SignatureModule
  ) => {
    setIsUploading(true);
    setStatus(ValidationStatus.default);
    setValidationMessage("");

    if (!fileLink) {
      setStatus(ValidationStatus.error);
      setValidationMessage("Project has not file to delete.");
      return;
    }

    try {
      await deleteFile(uuid, fileLink, module);
      setStatus(ValidationStatus.default);
      setValidationMessage("File deleted successfully.");
    } catch (e) {
      setValidationMessage("An error has occurred. Try again.");
      setStatus(ValidationStatus.error);
    }

    setIsUploading(false);
  };

  const handleUploadStart = (totalFiles: number, totalSize: number) => {
    setIsUploading(true);
    setTotalFiles(totalFiles);
    setTotalSize(totalSize);
    setStatus(ValidationStatus.default);
    setValidationMessage("");
  };

  const handleUploadedFiles = (
    formValues: IFormValues,
    field: string,
    url: string,
    isGallery?: boolean
  ) => {
    setStatus(ValidationStatus.default);
    setValidationMessage("File uploaded successfully.");

    if (isGallery) {
      galleryUrlsArray.push({
        mediaUrl: url,
      });
    }

    formValues[field] = isGallery ? galleryUrlsArray : url;
  };

  const handleOnUploadedBytes = (uploadedBytes: number) => {
    setUploadedSize((prev) => prev + uploadedBytes);
  };

  const handleUploadSuccess = () => {
    setIsUploading(false);
  };

  const handleResetFileUpload = () => {
    setTotalFiles(0);
    setUploadedFiles(0);
    setUploadedSize(0);
  };

  return {
    getFilesUrls,
    deleteFileFromStorage,
    isUploading,
    validationMessage,
    status,
    totalFiles,
    totalSize,
    uploadedFiles,
    uploadedSize,
    handleResetFileUpload,
  };
};

export default useFileUpload;
